import React, { useEffect } from 'react';
import { AddressValidation } from '@js/formSettings/validations';
import { ErrorMessage } from '@hookform/error-message';
import { usePostalJp } from 'use-postal-jp';
import { RepresentativeFormData } from '@js/interfaces/index';
import { FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { prefectureOptions } from '@js/components/common/prefectures';


type Props = {
  register: UseFormRegister<RepresentativeFormData>
  watchPostalCode: string
  errors: FieldErrors<RepresentativeFormData>
  setValue: UseFormSetValue<RepresentativeFormData>
};

export const Address = (props: Props) => {
  const {
    register,
    setValue,
    watchPostalCode,
    errors
  } = props;
  const [ address ] = usePostalJp(watchPostalCode, `${watchPostalCode}`.length > 6);

  useEffect(() => {
    if(address) {
      const prefecture = prefectureOptions.filter(prefecture => Number(prefecture.code) === Number(address.prefectureCode));
      if(prefecture.length < 1) return; //空配列が渡ってくるとエラーになってフォームが表示されなくなるため
      setValue('prefecture_name', prefecture[0].name || '選択して下さい');
      setValue('city', address.address1 + address.address2 + address.address3);
    }
  }, [address, setValue]);  
  return (
    <>
      <div className="form-input__wrap">
        <label className="form-label" htmlFor={'postal_code'}>
          <span className="form-label__required">必須</span>郵便番号
          <a className="form-label__link ml16" target="_blank" href="https://www.post.japanpost.jp/zipcode/index.html" rel="noreferrer">
            <i className="material-icons form-label__link-icon">search</i>
            郵便番号を調べる
          </a>
        </label>
        <div className="form-column">
          <div className="form-column__item">
            <input
              {...register('postal_code', AddressValidation.postalCode)}
              onWheel={(e) => e.currentTarget.blur()}
              className={`form-input ${errors['postal_code'] && 'is-error'}`}
              placeholder="例）1234567"
              type="number"
              id={'postal_code'}
            />
          </div>
        </div>
        <ErrorMessage
          errors={errors}
          name={'postal_code'}
          render={({ message }) =>
            <span className="form-input__error">郵便番号は{message}</span>}
        />
        <span className="form-input__note">※ハイフン無しでご入力ください</span>
      </div>
      <div className="form-input__wrap">
        <label className="form-label" htmlFor={'prefecture_name'}>
          <span className="form-label__required">必須</span>都道府県
        </label>
        <div className="form-column">
          <div className="form-column__item">
            <div className="form-select__wrap">
              <select
                {...register('prefecture_name', AddressValidation.prefectureName)}
                className={`form-select ${errors['prefecture_name'] && 'is-error'}`}
                id={'prefecture_name'}
              >
                {prefectureOptions.map((prefecture) => {
                  return <option value={prefecture.name} key={prefecture.code} >{prefecture.name}</option>;
                })}
              </select>
            </div>
          </div>
        </div>
        <ErrorMessage
          errors={errors}
          name={'prefecture_name'}
          render={({ message }) =>
            <span className="form-input__error">都道府県は{message}</span>}
        />
      </div>
      <div className="form-input__wrap">
        <label className="form-label" htmlFor={'city'}>
          <span className="form-label__required">必須</span>市区町村
        </label>
        <input
          {...register('city', AddressValidation.city)}
          type="text"
          id={'city'}
          className={`form-input ${errors['city'] && 'is-error'}`}
          placeholder="例）渋谷区恵比寿南"
        />
        <ErrorMessage
          errors={errors}
          name={'city'}
          render={({ message }) =>
            <span className="form-input__error">市区町村は{message}</span>}
        />
      </div>
      <div className="form-input__wrap">
        <label className="form-label" htmlFor={'street'}>
          <span className="form-label__required">必須</span>番地
        </label>
        <input
          {...register('street', AddressValidation.street)}
          type="text"
          id={'street'}
          className={`form-input ${errors['street'] && 'is-error'}`}
          placeholder="例）1-2-3"
        />
        <ErrorMessage
          errors={errors}
          name={'street'}
          render={({ message }) =>
            <span className="form-input__error">番地は{message}</span>}
        />
      </div>
      <div className="form-input__wrap">
        <label className="form-label" htmlFor={'building'}>
          <span className="form-label__conditional-required">該当する方は必須</span>建物名、部屋番号など
        </label>
        <input
          {...register('building', AddressValidation.building)}
          type="text"
          id={'building'}
          className={`form-input ${errors['building'] && 'is-error'}`}
          placeholder="例）恵比寿ビルディング　505号室"
        />
        <ErrorMessage
          errors={errors}
          name={'building'}
          render={({ message }) =>
            <span className="form-input__error">建物名は{message}</span>}
        />
        <span className="form-input__note">※建物名や部屋番号がある場合は必ずご入力ください</span>
      </div>
    </>
  );
};
